import { formatLocalDate } from '../utils/localDateTime';
import { showAppAlert } from '../common/appDialog';

import React, { useState } from 'react';
import { db, auth } from '../../firebaseConfig';
import { collection, query, where, doc, getDocs, deleteDoc, updateDoc } from 'firebase/firestore';
import type { SrmProject, Organization } from '@types';
import Loader from './Loader';
import { FrameworkDecommissionModal } from './FrameworkDecommissionModal';

interface SrmProjectsListProps {
    onSelectProject: (project: SrmProject) => void;
    onBack: () => void;
    organization: Organization | null;
    projects: SrmProject[];
    activeClientId?: string;
    activeClientName?: string;
    onClearClientFilter?: () => void;
}

const ADMIN_EMAIL = 'insights@inteloptics.org';

const exportProjectJson = (project: SrmProject) => {
    const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `srm-framework-${project.id}-export.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// ── Section config ────────────────────────────────────────────────────────────

const SECTIONS = [
    {
        key: 'active',
        label: 'Active Monitoring',
        match: (s: string) => s === 'Active',
        color: 'emerald',
        dot: 'bg-emerald-500',
        badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        ring: 'hover:border-emerald-400',
    },
    {
        key: 'config',
        label: 'Configuration In Progress',
        match: (s: string) => s === 'Review' || s === 'Draft',
        color: 'amber',
        dot: 'bg-amber-400',
        badge: 'bg-amber-50 text-amber-700 border-amber-200',
        ring: 'hover:border-amber-400',
    },
    {
        key: 'concluded',
        label: 'Concluded',
        match: (s: string) => s === 'Concluded',
        color: 'slate',
        dot: 'bg-slate-400',
        badge: 'bg-slate-100 text-slate-600 border-slate-200',
        ring: 'hover:border-slate-400',
    },
] as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

const getConfigProgress = (project: SrmProject): { done: number; total: number; pct: number } => {
    const total = project.modules?.length || 0;
    const done = (project.modules || []).filter(m => m.status === 'Ready' || m.status === 'Active').length;
    return { done, total, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
};

const getComplianceScore = (project: SrmProject): number => {
    const modules = project.modules || [];
    if (!modules.length) return 0;
    const ready = modules.filter(m => m.status === 'Ready' || m.status === 'Active').length;
    return Math.round((ready / modules.length) * 100);
};

// ── Component ─────────────────────────────────────────────────────────────────

const SrmProjectsList: React.FC<SrmProjectsListProps> = ({
    projects, onSelectProject, onBack, organization, activeClientId, activeClientName, onClearClientFilter,
}) => {
    const [archivingId, setArchivingId]           = useState<string | null>(null);
    const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);
    const [showArchived, setShowArchived]         = useState(false);
    const [decommissionProject, setDecommissionProject] = useState<SrmProject | null>(null);

    const allActive = projects.filter(p => (p.status as string) !== 'Archived');
    const archived  = projects.filter(p => (p.status as string) === 'Archived');

    const scoped = (list: SrmProject[]) =>
        activeClientId ? list.filter(p => p.clientId === activeClientId) : list;

    // ── Archive (soft) ────────────────────────────────────────────────────────

    const confirmArchive = async (id: string) => {
        if (!db) return;
        const project = projects.find(p => p.id === id);
        if (!project) return;
        setArchivingId(id);
        setArchiveConfirmId(null);
        try {
            const now = new Date().toISOString();
            const retentionYears = 1;
            const retentionExpiresAt = new Date(Date.now() + retentionYears * 365.25 * 24 * 60 * 60 * 1000).toISOString();
            const archiveLog = {
                id: `log-archive-${Date.now()}`,
                timestamp: now,
                user: auth.currentUser?.email || 'System',
                action: 'Framework Archived',
                details: `Archived by ${auth.currentUser?.email || 'operator'}. Retained until ${formatLocalDate(retentionExpiresAt)}.`,
            };
            await updateDoc(doc(db, 'srm_projects', id), {
                status: 'Archived', archivedAt: now,
                archivedBy: auth.currentUser?.email || 'unknown',
                retentionYears, retentionExpiresAt,
                auditLog: [...((project as any).auditLog || []), archiveLog],
            });
        } catch (e) {
            console.error('[SRM] Archive failed:', e);
            void showAppAlert('Archive failed. Please try again.');
        } finally { setArchivingId(null); }
    };

    // ── Permanent decommission — now handled by FrameworkDecommissionModal ─────

    // ── Card renderer ─────────────────────────────────────────────────────────

    const renderCard = (project: SrmProject, section: typeof SECTIONS[number] | null, isArchivedView = false) => {
        const isBusy = archivingId === project.id;
        const status = (project.status as string) || 'Review';
        const isConfig = status === 'Review' || status === 'Draft';
        const progress = isConfig ? getConfigProgress(project) : null;
        const compliance = getComplianceScore(project);

        return (
            <div
                key={project.id}
                onClick={() => onSelectProject(project)}
                className={`group bg-white p-5 rounded-xl border transition-all cursor-pointer flex flex-col md:flex-row justify-between items-start md:items-center gap-4 ${
                    isArchivedView ? 'border-slate-200 opacity-55' : `border-slate-200 hover:shadow-md ${section?.ring || 'hover:border-blue-400'}`
                }`}
            >
                {/* Left: info */}
                <div className="flex items-start gap-4 flex-1 min-w-0">
                    {/* Status dot */}
                    <div className={`mt-1.5 flex-shrink-0 w-2.5 h-2.5 rounded-full ${isArchivedView ? 'bg-slate-300' : (section?.dot || 'bg-blue-500')}`} />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-slate-800 group-hover:text-blue-700 transition-colors truncate">
                                {project.name || project.projectName || 'Untitled'}
                            </h3>
                            {project.projectNumber && (
                                <span className="text-[10px] font-mono text-slate-400">[{project.projectNumber}]</span>
                            )}
                            {isArchivedView && (
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-bold rounded border border-slate-200">ARCHIVED</span>
                            )}
                        </div>

                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{project.summary}</p>

                        {/* Config progress bar */}
                        {progress && !isArchivedView && (
                            <div className="mt-2 flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-[160px]">
                                    <div
                                        className="h-full bg-amber-400 rounded-full transition-all"
                                        style={{ width: `${progress.pct}%` }}
                                    />
                                </div>
                                <span className="text-[10px] font-semibold text-amber-600">
                                    {progress.done}/{progress.total} modules configured
                                </span>
                            </div>
                        )}

                        {/* Compliance for active/concluded */}
                        {!progress && !isArchivedView && compliance > 0 && (
                            <div className="mt-1.5 flex items-center gap-1.5">
                                <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all ${compliance >= 80 ? 'bg-emerald-500' : compliance >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                                        style={{ width: `${compliance}%` }}
                                    />
                                </div>
                                <span className="text-[10px] text-slate-500 font-semibold">{compliance}% compliance</span>
                            </div>
                        )}

                        {/* Meta */}
                        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-400 font-mono flex-wrap">
                            <span>CREATED {formatLocalDate(project.createdAt)}</span>
                            {isArchivedView && (project as any).archivedAt && (
                                <><span>&bull;</span><span>ARCHIVED {formatLocalDate((project as any).archivedAt)}</span></>
                            )}
                            {isArchivedView && (project as any).retentionExpiresAt && (
                                <><span>&bull;</span><span>RETAIN UNTIL {formatLocalDate((project as any).retentionExpiresAt)}</span></>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: actions */}
                <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    {isArchivedView ? (
                        <>
                            <button onClick={() => onSelectProject(project)} className="px-3 py-1.5 text-xs font-semibold text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                                View Record
                            </button>
                            <button
                                onClick={e => { e.stopPropagation(); setDecommissionProject(project); }}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                title="Decommission Framework (structured deletion with audit trail)"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                        </>
                    ) : (
                        <>
                            <span className={`hidden sm:inline-flex px-2 py-0.5 text-[10px] font-bold rounded border ${section?.badge || 'bg-blue-50 text-blue-600 border-blue-200'}`}>
                                {status === 'Active' ? 'Active' : status === 'Concluded' ? 'Concluded' : 'Draft'}
                            </span>
                            <button
                                onClick={() => setArchiveConfirmId(project.id)}
                                disabled={isBusy}
                                className="p-2 text-slate-300 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all"
                                title="Archive Framework"
                            >
                                {archivingId === project.id
                                    ? <div className="h-4 w-4 border-2 border-amber-200 border-t-amber-500 animate-spin rounded-full" />
                                    : <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8l1 12a2 2 0 002 2h8a2 2 0 002-2L19 8" /></svg>
                                }
                            </button>
                            <button
                                onClick={e => { e.stopPropagation(); setDecommissionProject(project); }}
                                className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                title="Decommission Framework (structured deletion with audit trail)"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    };

    // ── Render ────────────────────────────────────────────────────────────────

    const scopedActive = scoped(allActive);
    const scopedArchived = scoped(archived);
    const hasAnyActive = scopedActive.length > 0;

    return (
        <div className="animate-fade-in max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-start mb-8">
                <div>
                    <button onClick={onBack} className="flex items-center text-sm font-medium text-slate-500 hover:text-blue-600 transition-colors mb-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        ECC Hub
                    </button>
                    <h2 className="text-2xl font-black text-slate-900">SRM Framework Command</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Security Risk Management lifecycle — {allActive.length} active framework{allActive.length !== 1 ? 's' : ''}</p>
                </div>
            </div>

            {/* Client scope banner */}
            {activeClientId && activeClientName && (
                <div className="mb-6 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl px-5 py-3">
                    <div className="flex items-center gap-3">
                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                        <p className="text-sm font-bold text-blue-700">Scoped to <span className="font-black">{activeClientName}</span></p>
                        <span className="text-[10px] font-bold text-blue-500 bg-blue-100 px-2 py-0.5 rounded">{scopedActive.length} project{scopedActive.length !== 1 ? 's' : ''}</span>
                    </div>
                    {onClearClientFilter && (
                        <button onClick={onClearClientFilter} className="text-xs font-bold text-blue-500 hover:underline">Show all</button>
                    )}
                </div>
            )}

            {/* Empty state */}
            {!hasAnyActive && (
                <div className="text-center py-20 bg-slate-50 rounded-2xl border border-dashed border-slate-300">
                    <div className="mx-auto w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800">No Active Frameworks</h3>
                    <p className="text-sm text-slate-500 mt-1">Use the Auto-Architect to generate your first SRM framework.</p>
                </div>
            )}

            {/* Sectioned project lists */}
            {SECTIONS.map(section => {
                const sectionProjects = scopedActive.filter(p => section.match((p.status as string) || 'Review'));
                if (sectionProjects.length === 0) return null;
                return (
                    <div key={section.key} className="mb-8">
                        {/* Section header */}
                        <div className="flex items-center gap-3 mb-3">
                            <span className={`w-2 h-2 rounded-full ${section.dot}`} />
                            <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest">{section.label}</h3>
                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${section.badge}`}>{sectionProjects.length}</span>
                            <div className="flex-1 h-px bg-slate-100" />
                        </div>
                        <div className="space-y-3">
                            {sectionProjects.map(p => renderCard(p, section, false))}
                        </div>
                    </div>
                );
            })}

            {/* Archived section */}
            {scopedArchived.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                    <button
                        onClick={() => setShowArchived(v => !v)}
                        className="flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors mb-3"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-3.5 w-3.5 transition-transform ${showArchived ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        Show Archived Frameworks ({scopedArchived.length})
                    </button>
                    {showArchived && (
                        <div className="space-y-3">
                            {scopedArchived.map(p => renderCard(p, null, true))}
                        </div>
                    )}
                </div>
            )}

            {/* Archive Confirm Modal */}
            {archiveConfirmId && (() => {
                const p = projects.find(x => x.id === archiveConfirmId);
                return p ? (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7 border border-slate-200">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2.5 bg-amber-50 rounded-xl">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8l1 12a2 2 0 002 2h8a2 2 0 002-2L19 8" /></svg>
                                </div>
                                <h3 className="text-base font-black text-slate-900">Archive Framework?</h3>
                            </div>
                            <p className="text-sm text-slate-600 mb-3"><strong className="text-slate-800">{p.name || p.projectName}</strong> will move to read-only archive and be hidden from active views.</p>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-4">
                                <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest mb-1">⚖️ Compliance Notice</p>
                                <p className="text-xs text-amber-700">Record retained for <strong>1 year</strong>. Verify this meets your organisation's US federal/state regulatory requirements (NIST/FISMA recommend 3–7 years).</p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => setArchiveConfirmId(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50">Cancel</button>
                                <button onClick={() => confirmArchive(archiveConfirmId)} className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold">Archive</button>
                            </div>
                        </div>
                    </div>
                ) : null;
            })()}

            {decommissionProject && (
                <FrameworkDecommissionModal
                    isOpen={true}
                    project={decommissionProject}
                    organization={organization}
                    onClose={() => setDecommissionProject(null)}
                    onComplete={() => setDecommissionProject(null)}
                />
            )}
        </div>
    );
};

export default SrmProjectsList;
